import { PlusOutlined } from "@ant-design/icons";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Button, Flex, Table, Tag, Typography } from "antd";
import { useNavigate } from "react-router";
import { api, unwrap, type Schemas } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { ExportButton } from "@/components/list/ExportButton";
import { DateRangeFilter, SearchFilter, StatusFilter, useSearchFilter, useStatusFilter } from "@/components/list/filters";
import { useDateRange, useListPage } from "@/components/list/useListPage";
import { useI18n } from "@/i18n";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { SupplierSelect } from "./common";

export type VendorReturn = Schemas["VendorReturn"];
export type VendorReturnRow = Schemas["VendorReturnRow"];
export type VendorReturnStatus = Schemas["VendorReturnStatus"];
export const VR_STATUSES: VendorReturnStatus[] = ["draft", "ready", "shipped", "closed", "cancelled"];
export const VR_REASONS: Schemas["VendorReturnReason"][] = ["damaged", "wrong_item", "expired", "quality", "excess", "other"];

const statusColor: Record<VendorReturnStatus, string> = { draft: "default", ready: "gold", shipped: "processing", closed: "success", cancelled: "error" };

export function VrStatusTag({ status }: { status: VendorReturnStatus }) {
  const { t } = useI18n();
  return <Tag color={statusColor[status]}>{t(`vr.status.${status}`)}</Tag>;
}

const STATUS_CHOICES = ["open", "draft", "ready", "shipped", "closed", "cancelled", "all"] as const;
const STATUS_PARAM: Record<(typeof STATUS_CHOICES)[number], string | undefined> = {
  open: "draft,ready,shipped",
  draft: "draft",
  ready: "ready",
  shipped: "shipped",
  closed: "closed",
  cancelled: "cancelled",
  all: undefined,
};

/** Pembelian > Retur ke supplier (Plan §6.18). */
export function VendorReturns() {
  const { t, formatDate, formatMoney, formatNumber } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const status = useStatusFilter(STATUS_CHOICES, "open");
  const search = useSearchFilter();
  const range = useDateRange();
  const page = useListPage();
  const [supplierId, setSupplierId] = useUrlFilter<string>("supplierId", "");

  const statusParam = STATUS_PARAM[status.value];
  const filters = { ...(statusParam ? { status: statusParam } : {}), ...(supplierId ? { supplierId } : {}), ...search.query, ...range.query };
  const list = useQuery({
    queryKey: ["vendor-returns", filters, page.query],
    queryFn: () => unwrap(api.GET("/v1/vendor-returns", { params: { query: { ...filters, ...page.query } } })),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("vr.title")}
        </Typography.Title>
        {can("procurement.manage") && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/app/procurement/returns/new")}>
            {t("vr.add")}
          </Button>
        )}
      </Flex>
      <Typography.Paragraph type="secondary">{t("vr.subtitle")}</Typography.Paragraph>
      <Flex gap={12} wrap style={{ marginBottom: 16 }}>
        <SearchFilter filter={search} placeholder={t("vr.search")} />
        <StatusFilter
          filter={status}
          options={[
            { value: "open", label: t("vr.filter.open") },
            ...VR_STATUSES.map((s) => ({ value: s, label: t(`vr.status.${s}`) })),
            { value: "all", label: t("list.allStatuses") },
          ]}
        />
        <SupplierSelect value={supplierId || undefined} onChange={(id) => setSupplierId(id ?? "")} allowClear style={{ width: 240 }} />
        <DateRangeFilter range={range} />
        <div style={{ flex: 1 }} />
        <ExportButton
          path="/v1/vendor-returns/export"
          query={filters}
          filename={t("vr.title")}
          columns={[
            { key: "number", label: t("vr.number") },
            { key: "supplierName", label: t("po.supplier") },
            { key: "warehouseName", label: t("po.warehouse") },
            { key: "status", label: t("common.status"), values: Object.fromEntries(VR_STATUSES.map((v) => [v, t(`vr.status.${v}`)])) },
            { key: "reason", label: t("vr.reason"), values: Object.fromEntries(VR_REASONS.map((v) => [v, t(`vr.reason.${v}`)])) },
            { key: "settlement", label: t("vr.settlement"), values: { credit: t("vr.settlement.credit"), replace: t("vr.settlement.replace") } },
            { key: "units", label: t("po.quantity"), kind: "number" },
            { key: "total", label: t("po.total"), kind: "number" },
            { key: "createdName", label: t("po.createdBy") },
            { key: "createdAt", label: t("list.createdAt"), kind: "datetime" },
            { key: "shippedAt", label: t("vr.shippedAt"), kind: "datetime" },
          ]}
        />
      </Flex>
      <Table<VendorReturnRow>
        rowKey="id"
        loading={list.isFetching}
        dataSource={list.data?.items}
        pagination={page.pagination(list.data)}
        locale={{ emptyText: t("vr.empty") }}
        onRow={(v) => ({ onClick: () => navigate(`/app/procurement/returns/${v.id}`), style: { cursor: "pointer" } })}
        columns={[
          { title: t("vr.number"), dataIndex: "number", width: 140, render: (v: string) => <Typography.Text strong className="z-num">{v}</Typography.Text> },
          { title: t("po.supplier"), dataIndex: "supplierName" },
          { title: t("po.warehouse"), dataIndex: "warehouseName", width: 170 },
          { title: t("vr.reason"), dataIndex: "reason", width: 150, render: (v: VendorReturnRow["reason"]) => t(`vr.reason.${v}`) },
          { title: t("vr.settlement"), dataIndex: "settlement", width: 150, render: (v: VendorReturnRow["settlement"]) => t(`vr.settlement.${v}`) },
          { title: t("common.status"), dataIndex: "status", width: 130, render: (v: VendorReturnStatus) => <VrStatusTag status={v} /> },
          { title: t("po.quantity"), dataIndex: "units", width: 90, align: "right", render: (v: number) => <span className="z-num">{formatNumber(v)}</span> },
          { title: t("list.createdAt"), dataIndex: "createdAt", width: 120, render: (v: string) => formatDate(v) },
          { title: t("po.total"), dataIndex: "total", width: 140, align: "right", render: (v: number) => <span className="z-num">{formatMoney(v)}</span> },
        ]}
      />
    </>
  );
}
